import {useCallback, useMemo, useRef, useState} from 'react'

// Molecules
import {PageHeader, TabsComponent} from '@/components/molecules'

// Organisms
import {
  CustomDataTable,
  FileUploadWithModal,
  PageContainer,
  UploadHistoryTableModal,
  UploadMediaModal,
} from '@/components/organisms'

// Helpers
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper'

// Hooks
import {useCountryProductTabs} from '@/hooks/useCountryProductTabs'
import {useProductList} from '@/hooks/useProductList'

// Components
import {LinkText} from '@/components/atoms'

// Constants
import {FONT_SIZE} from '@/utils/constants/globalStyle'

// Styles
import '@/assets/scss/custom/components/_productList.scss'
import {MAIN_COLUMNS} from '@/containers/Fulfillment/InventoryList/InventoryListConstants'
import {useProductButtonContainer} from '@/hooks/ProductButtonContainer-hook'
import {useSuggestedSKU} from '@/hooks/useSuggestedSKU.jsx'
import useUploadHistory from '@/hooks/useUploadHistory'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {Constants, DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {PRODUCT_TYPE, UPLOAD_HISTORY_TYPE} from '@/utils/constants/globalConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'

const toSafeRouteParam = value => {
  const raw = String(value ?? '').trim()

  if (!raw || raw.includes('..') || raw.includes('/') || raw.includes('\\')) {
    return ''
  }

  if (!/^[A-Za-z0-9._-]+$/.test(raw)) {
    return ''
  }

  return encodeURIComponent(raw)
}

const cellDataMapperObj = (navigate, targetRoute = ROUTES.PRODUCT_REVIEW) => ({
  productId: ({row: {original} = {}}) => {
    const safeProductId = toSafeRouteParam(original?.productId)

    return (
      <div>
        <LinkText
          text={original?.productId}
          id={original?.productId + ' ' + original?.productId}
          handleClick={() => {
            if (safeProductId) {
              navigate(`${targetRoute.replace(':id', safeProductId)}`)
            }
          }}
        />
      </div>
    )
  },

  productName: ({row: {original} = {}}) => {
    const safeProductId = toSafeRouteParam(original?.productId)

    return (
      <div>
        <LinkText
          text={original?.productName}
          id={original?.productName + ' ' + original?.id}
          maxWidth="350px"
          showTooltip={true}
          handleClick={() => {
            if (safeProductId) {
              navigate(`${targetRoute.replace(':id', safeProductId)}`)
            }
          }}
        />
      </div>
    )
  },
  displayName_en: ({row: {original} = {}}) => {
    return <div>{original?.displayName_en ?? '-'}</div>
  },
  imageAvailability: ({row: {original} = {}}) => {
    const imageAvailability =
      original?.imageAvailability === true ? 'Yes' : original?.imageAvailability === false ? 'No' : '-'
    return <div>{imageAvailability}</div>
  },
  subCategory_en: ({row: {original} = {}}) => {
    return <div>{original?.subCategory_en ?? '-'}</div>
  },
  suggestedProductSellingPrice: ({row: {original} = {}}) => {
    return <div>{original?.suggestedProductSellingPrice ?? '-'}</div>
  },
  multipleSellingUnits: ({row: {original} = {}}) => {
    return <div>{original?.multipleSellingUnits ?? '-'}</div>
  },
})

const subColumns = [
  {
    columnKey: 'productId',
    header: 'Product ID',
  },
  {
    columnKey: 'errorMsg',
    header: 'STATUS (REMARK)',
  },
]

export const ProductList = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const defaultTabIndex = location?.state?.selectedTabNumb ?? 0
  const productTableRef = useRef(null)
  const countryCode = useSelector(selectCountryCode)
  const [isSelectAll, setSelectAll] = useState(false)
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  const [productType, setProductType] = useState(PRODUCT_TYPE.BRAND)
  const [rowSelected, setRowSelected] = useState([])
  const {activeProductTabIndex, handleProductTabChange, productTabList, productTabFilters} = useCountryProductTabs(
    countryCode,
    productTableRef
  )
  const {
    productListData,
    productListColConfigData,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    onPageIndexChange,
    comboProductListColConfigData,
    isLoading,
    createSearchArray,
    resetAllBtnClicked,
    fetchUploadErrors,
    setActiveTabIndex,
    activeTabIndex,
    comboProductListData,
    isComboLoading,
  } = useProductList(defaultTabIndex, productTabFilters)
  const {
    setFormData,
    formData,
    toggleMultipleImagesModal,
    isMultipleImagesModal,
    isProductListUploadModal,
    toggleProductListUploadModal,
    onDownloadProductEnrichmentTemplate,
  } = useProductButtonContainer()
  const {onUploadHistoryPageIndexChange, uploadHistoryData, downloadUploadError} = useUploadHistory(
    UPLOAD_HISTORY_TYPE?.PRODUCT,
    openUploadHistory
  )
  const {
    downloadSuggestedSKUTemplate,
    setShowSKUModal,
    showSKUModal,
    setFormData: setSKUModalFormData,
    formData: SKUModalFormData,
  } = useSuggestedSKU()

  const productCellDataMappedCols = getCellDataMappedCols(cellDataMapperObj(navigate), productListColConfigData)
  const comboCellDataMappedCols = getCellDataMappedCols(
    cellDataMapperObj(navigate, ROUTES.PRODUCT_COMBO_DETAIL),
    comboProductListColConfigData
  )
  const cellDataMappedCols = productCellDataMappedCols

  const handleSorting = ({column, direction}) => {
    setSortingObj({column, direction})
  }

  const handleSearchArry = useCallback(
    searchObj => {
      if (searchObj?.field !== 'imageAvailability') {
        setSearchArry(searchObj)
        return
      }

      const rawValue = searchObj?.value
      const normalizedValue = typeof rawValue === 'string' ? rawValue.trim().toLowerCase() : rawValue

      const booleanMap = {
        yes: true,
        no: false,
        true: true,
        false: false,
      }

      if (normalizedValue === '') {
        setSearchArry({...searchObj, value: ''})
        return
      }

      if (normalizedValue in booleanMap) {
        setSearchArry({...searchObj, value: booleanMap[normalizedValue]})
        return
      }

      setSearchArry(searchObj)
    },
    [setSearchArry]
  )

  // Handle row selection for CustomDataTable
  const handleRowSelect = selectedRows => {
    setRowSelected(selectedRows)
  }

  // Handle page index change for CustomDataTable
  const handlePageIndexChange = useCallback(
    pageIndex => {
      onPageIndexChange(pageIndex)
    },
    [onPageIndexChange]
  )

  const handleResetAll = useCallback(() => {
    setSortingObj({})
    createSearchArray([])
    setRowSelected([])
    setSelectAll(false)
    handlePageIndexChange(1)
    resetAllBtnClicked()
  }, [setSortingObj, createSearchArray, handlePageIndexChange, resetAllBtnClicked])

  const transformedComboColumns = useMemo(
    () =>
      comboCellDataMappedCols.map(col => ({
        ...col,
        name_of_db_column: col.name_of_db_column,
        label_of_column: col.label_of_column,
        filterVariant: col.filterVariant || 'text',
        // Add clickable configuration for productId
        ...(col.name_of_db_column === 'productId' && {
          isClickable: true,
          onCellClick: value => {
            const safeValue = toSafeRouteParam(value)

            if (safeValue) {
              navigate(ROUTES.PRODUCT_COMBO_DETAIL.replace(':id', safeValue))
            }
          },
        }),
      })),
    [comboCellDataMappedCols, navigate]
  )
  const transformedColumns = useMemo(
    () =>
      cellDataMappedCols.map(col => ({
        ...col,
        name_of_db_column: col.name_of_db_column,
        label_of_column: col.label_of_column,
        filterVariant: col.filterVariant || 'text',
        // Add clickable configuration for productId
        ...(col.name_of_db_column === 'productId' && {
          isClickable: true,
          onCellClick: value => {
            const safeValue = toSafeRouteParam(value)

            if (safeValue) {
              navigate(ROUTES.PRODUCT_REVIEW.replace(':id', safeValue))
            }
          },
        }),
      })),
    [cellDataMappedCols, navigate]
  )
  const openProductType = useCallback(
    type => {
      setProductType(type)
      toggleMultipleImagesModal(true)
    },
    [toggleMultipleImagesModal, setProductType]
  )
  const tableActions = useMemo(
    () => [
      {
        id: 'upload-product-list',
        label: `Upload Product List`,
        onClick: () => toggleProductListUploadModal(true),
      },
      {
        id: 'upload-multiple-images',
        label: `Upload Multiple Images`,
        onClick: () => openProductType(PRODUCT_TYPE.PRODUCT),
      },
      {
        id: 'download-product-enrichment-template',
        label: 'Download Product List Template',
        onClick: onDownloadProductEnrichmentTemplate,
      },
      {
        id: 'brand',
        label: 'Upload Brand Images',
        onClick: () => openProductType(PRODUCT_TYPE.BRAND),
      },
      {
        id: 'subbrand',
        label: 'Upload Sub Brand Images',
        onClick: () => openProductType(PRODUCT_TYPE.SUBBRAND),
      },
      {
        id: 'displaybrand',
        label: 'Upload Display Brand Images',
        onClick: () => openProductType(PRODUCT_TYPE.DISPLAYBRAND),
      },
      {
        id: 'category',
        label: 'Upload Category Images',
        onClick: () => openProductType(PRODUCT_TYPE.CATEGORY),
      },
      {
        id: 'subcategory',
        label: 'Upload Sub Category Images',
        onClick: () => openProductType(PRODUCT_TYPE.SUBCATEGORY),
      },
      {
        id: 'displaycategory',
        label: 'Upload Display Category Images',
        onClick: () => openProductType(PRODUCT_TYPE.DISPLAYCATEGORY),
      },
      {
        id: 'view-upload-history',
        label: 'View Uploaded History',
        onClick: () => setOpenUploadHistory(true),
      },
      {
        id: 'upload-suggested-skus',
        label: `Suggested SKU's`,
        onClick: () => setShowSKUModal(true),
      },
      {
        id: 'download',
        label: 'Download',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelected
          const dataToDownload = isSelectAll ? [] : rowSelected
          const productIds = dataToDownload.map(product => product.productId)

          onDownloadBtnClick(productIds)
        },
        disabled: ({selectedRows}) => !selectedRows?.length, // Dynamic disable based on selection
      },
    ],
    [openProductType, isSelectAll, rowSelected, onDownloadBtnClick, setShowSKUModal, toggleMultipleImagesModal]
  )
  const ComboProductTableActions = useMemo(
    () => [
      {
        id: 'create_combp_prod',
        label: 'Create Combo',
        onClick: () => {
          navigate(`${ROUTES.CREATE_PRODUCT_COMBINATION}`) // Replace :id with the actual productId
        },
      },
    ],
    [navigate]
  )

  const handleTabChange = useCallback(
    index => {
      if (index === activeTabIndex) {
        return
      }

      handleResetAll()
      setActiveTabIndex(index)
    },
    [activeTabIndex, handleResetAll, setActiveTabIndex]
  )

  const columns = MAIN_COLUMNS(downloadUploadError)

  const productListTable = (
    <CustomDataTable
      ref={productTableRef}
      columns={transformedColumns}
      records={productListData?.data || []}
      setSearchArry={handleSearchArry}
      handleSorting={handleSorting}
      setPageIndex={handlePageIndexChange}
      totalRecords={productListData?.totalRecords || 0}
      pageSize={DEFAULT_TABLE_PAGE_SIZE}
      onResetAll={handleResetAll}
      onRowSelect={handleRowSelect}
      isLoading={isLoading}
      showActions={true}
      id="productId"
      actions={tableActions}
      isSelectAll={isSelectAll}
      setSelectAll={setSelectAll}
      defaultSortConfig={{key: 'productId', direction: 'desc'}}
    />
  )

  const tabList = [
    {
      id: 'Product List',
      tabLabel: 'Product List',
      title: 'Product List',
      tabComponent: (
        <div key="product-list-tab" className="ProductListDataTable">
          <PageHeader title="Product List" fontSize={FONT_SIZE.XXL} />
          {productTabList?.length ? (
            <TabsComponent
              tabList={productTabList.map(item => ({
                ...item,
                tabComponent: productListTable,
              }))}
              defaultTabIndex={activeProductTabIndex}
              onTabChange={handleProductTabChange}
            />
          ) : (
            productListTable
          )}
        </div>
      ),
    },
    {
      id: 'Combo List',
      tabLabel: 'Combo List',
      title: 'Combo List',
      tabComponent: (
        <div key="combo-list-tab" className="ComboProductListDataTable">
          <PageHeader title="Combo List" fontSize={FONT_SIZE.XXL} />
          <CustomDataTable
            enableSelection={false}
            columns={transformedComboColumns}
            records={comboProductListData?.data || []}
            setSearchArry={handleSearchArry}
            handleSorting={handleSorting}
            setPageIndex={handlePageIndexChange}
            totalRecords={comboProductListData?.totalRecords || 0}
            pageSize={DEFAULT_TABLE_PAGE_SIZE}
            onResetAll={handleResetAll}
            onRowSelect={handleRowSelect}
            isLoading={isComboLoading}
            showActions={true}
            id="productId"
            actions={ComboProductTableActions}
            isSelectAll={isSelectAll}
            setSelectAll={setSelectAll}
            defaultSortConfig={{key: 'productId', direction: 'desc'}}
          />
        </div>
      ),
    },
  ]
  const allowedCountries = useFeatureValue(growthBookFeatureFlags.AllowedCountriesForComboList)?.[
    'allowedCountriesList'
  ] ?? ['MX', 'US']
  const finalTabList = allowedCountries?.includes(countryCode ?? 'MX')
    ? tabList
    : tabList.filter(tab => tab.id !== 'Combo List')
  return (
    <div className="ProductListContainer" data-mcp-ready="products-list">
      <PageHeader title="Products" fontSize={FONT_SIZE.XXXL} />
      {/* {activeTabIndex === 0 && <ProductButtonContainer />} */}
      <PageContainer data-testid="create-bulk-point-modifiers-content">
        <TabsComponent tabList={finalTabList} defaultTabIndex={activeTabIndex} onTabChange={handleTabChange} />
      </PageContainer>
      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData.data}
        totalRecords={uploadHistoryData.totalRecords}
        mainColumns={columns}
        subColumns={subColumns}
        fetchSubRows={fetchUploadErrors}
        isDark={true}
        setPageIndex={onUploadHistoryPageIndexChange}
        data-testid="upload-history-modal"
      />
      <UploadMediaModal
        openUpload={isMultipleImagesModal}
        setOpenUpload={toggleMultipleImagesModal}
        title="Upload Media"
        showTertiaryButton={true}
        formData={formData}
        setFormData={setFormData}
        entityType={productType}
      />
      <FileUploadWithModal
        openUpload={isProductListUploadModal}
        setOpenUpload={toggleProductListUploadModal}
        setFormData={setFormData}
        formData={formData}
        title="Upload Product List"
        showTertiaryButton={true}
        uploadFor={'product'}
        isCsvValidationRequired={true}
        isTemplateUpload={true}
        label={'Upload product file'}
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
      />
      <FileUploadWithModal
        openUpload={showSKUModal}
        setOpenUpload={setShowSKUModal}
        setFormData={setSKUModalFormData}
        formData={SKUModalFormData}
        title="Upload Suggested SKU's"
        showTertiaryButton={true}
        uploadFor={Constants.uploadSuggestedSKUs}
        isCsvValidationRequired={true}
        isTemplateUpload={true}
        label={`Suggested SKU's file (25 MB max).`}
        toastMessage={`File has successfully been uploadedSuggested SKU's Successfully Publishe`}
        tertiaryButtonText={'Download Template'}
        onClickTertiaryButton={() => downloadSuggestedSKUTemplate()}
        showTertiaryDownloadButton={true}
        acceptedExtensions={Constants.CSV}
      />
    </div>
  )
}
